import styled, { keyframes } from "styled-components";
import { useContext, useState } from "react";
import { useDispatch } from "react-redux";
import { cartActions } from "../../store/cart-slice";
import { ProductContext } from "../../context/ProductContext";
import AddToCartAnim from "../animations/AddToCartAnim";
import { useNavigation } from "../../utils/navigation";
import { useNavigate } from "react-router-dom";
import { calculatePrice } from "../../utils/discount";
import { useTranslation } from "react-i18next";

const Container = styled.figure`
  position: relative;
  background-color: var(--bg-200);
  width: 96%;
  margin: 0;
  padding: var(--spacing-lg) 0;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: var(--spacing-sm);
  border-radius: var(--border-radius-large);
  box-shadow: var(--shadow-small);
  transition: box-shadow var(--transition-fast);

  &:hover {
    box-shadow: var(--shadow-large);
  }
`;
const Picture = styled.picture`
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  width: 90%;
  max-height: 140px;
  min-height: 140px;
  position: relative;
  overflow: hidden;
  border: 1px solid var(--bg-300);
  border-radius: var(--border-radius-base);
  background-color: var(--bg-100);

  &:hover {
    .primary-image {
      display: ${(props) => (props.$secondary_image ? "none" : "block")};
    }
    .secondary-image {
      display: ${(props) => (props.$secondary_image ? "block" : "none")};
    }
  }
  @media (min-width: 768px) {
    max-height: 200px;
    min-height: 200px;
  }
`;

const Image = styled.img`
  width: 42vw;
  object-fit: contain;
  display: block;
  margin: 0 auto;
  opacity: ${({ $outOfStock }) => ($outOfStock ? 0.45 : 1)};
  filter: ${({ $outOfStock }) => ($outOfStock ? "grayscale(1)" : "none")};
  transition: transform var(--transition-fast);

  ${Picture}:hover & {
    transform: ${({ $outOfStock }) => ($outOfStock ? "none" : "scale(1.06)")};
  }

  &.secondary-image {
    display: none;
  }
  @media (min-width: 768px) {
    width: 220px;
  }
`;
const Bottom = styled.div`
  width: 90%;
`;

const ProductTitle = styled.h4`
  margin: var(--spacing-sm) 0;
  color: var(--text-100);
  font-size: var(--font-size-base);
  font-weight: 600;
  cursor: pointer;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  min-height: calc(var(--font-size-base) * 2.6);

  &:hover {
    color: var(--primary-200);
  }
`;

const QuantityLabel = styled.p`
  margin: 0 0 6px 0;
  font-family: inherit;
  font-size: var(--font-size-small);
  font-weight: 600;
  letter-spacing: 0.02em;
  color: var(--text-200);
`;

const GridQuantity = styled.div`
  display: flex;
  gap: 4px;
  padding: 4px;
  background-color: var(--bg-100);
  border: 1px solid var(--bg-300);
  border-radius: var(--border-radius-large);
`;
const GridQuantityItem = styled.button`
  flex: 1;
  padding: 7px 0;
  font-size: var(--font-size-small);
  font-weight: 700;
  color: ${({ $isActive }) => ($isActive ? "var(--bg-100)" : "var(--text-200)")};
  background-color: ${({ $isActive }) =>
    $isActive ? "var(--primary-200)" : "transparent"};
  border: none;
  border-radius: var(--border-radius-base);
  box-shadow: ${({ $isActive }) => ($isActive ? "var(--shadow-small)" : "none")};
  cursor: pointer;
  transition: background-color var(--transition-fast), color var(--transition-fast);

  &:hover {
    color: ${({ $isActive }) => ($isActive ? "var(--bg-100)" : "var(--primary-200)")};
  }
`;

const priceFadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(4px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const badgePop = keyframes`
  0% {
    opacity: 0;
    transform: scale(0.4);
  }
  70% {
    opacity: 1;
    transform: scale(1.15);
  }
  100% {
    transform: scale(1);
  }
`;

const PriceBlock = styled.div`
  margin-top: var(--spacing-md);
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const PriceRow = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
  min-height: 20px;
`;

const TotalPrice = styled.span`
  display: inline-block;
  font-size: var(--font-size-xlarge);
  font-weight: 700;
  color: var(--text-100);
  animation: ${priceFadeIn} 0.25s ease;
`;

const PerPiece = styled.span`
  display: inline-block;
  font-size: var(--font-size-small);
  color: var(--text-200);
  animation: ${priceFadeIn} 0.25s ease;
`;

const SavingsBadge = styled.span`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 999px;
  background-color: var(--accent-100);
  color: var(--bg-100);
  font-size: 11px;
  font-weight: 700;
  animation: ${badgePop} 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
`;

const OutOfStockBadge = styled.span`
  position: absolute;
  top: var(--spacing-sm);
  left: var(--spacing-sm);
  z-index: 1;
  padding: 3px 10px;
  border-radius: 999px;
  background-color: var(--bg-100);
  border: 1px solid var(--bg-300);
  color: var(--text-200);
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const ButtonWrapper = styled.div`
  position: relative;
  display: block;
  width: 100%;
  margin-top: var(--spacing-md);
`;
const Button = styled.button`
  width: 100%;
  text-transform: capitalize;
  z-index: 1;
  border-radius: var(--border-radius-base);
  font-size: 12px;
  @media (min-width: 768px) {
    font-size: 14px;
  }
`;

const ProductCard = ({ product }) => {
  const { t } = useTranslation();
  const [selectedQuantity, setSelectedQuantity] = useState(10);
  const dispatch = useDispatch();
  const { setIsCartOpen, maxAttributes, currencyTag } =
    useContext(ProductContext);
  const { goToProduct } = useNavigation();
  const navigate = useNavigate();

  const [isAnimating, setIsAnimating] = useState(false);

  const addToCart = (selectedQuantity) => {
    dispatch(
      cartActions.addToCart({
        product: product,
        quantity: selectedQuantity,
      })
    );
    setIsCartOpen(true);
    setIsAnimating(true);
  };

  const handleAnimationComplete = () => {
    setIsAnimating(false);
  };

  const discountedPrice = calculatePrice(product.price, selectedQuantity);

  // Calculate savings
  const savingsPercentage = Math.round(
    ((product.price - discountedPrice) / product.price) * 100
  );

  const primaryImage = product.images?.find((img) => img.is_primary);
  const secondaryImage = product.images?.find((img) => !img.is_primary);
  const isOutOfStock = product.is_in_stock === "out_of_stock";
  return (
    <Container>
      <Picture
        onClick={() =>
          navigate(goToProduct(product.category_name, product.name))
        }
        $secondary_image={secondaryImage}
      >
        {isOutOfStock && (
          <OutOfStockBadge>{t("BUTTONS.AVAILABLE_SOON")}</OutOfStockBadge>
        )}
        {primaryImage && (
          <Image
            src={primaryImage.thumbnail}
            alt={`${product.name}`}
            srcSet={`${primaryImage.thumbnail} 320w, ${primaryImage.large} 480w, ${primaryImage.large} 800w`}
            sizes="(max-width: 320px) 300px, (max-width: 480px) 440px, 800px"
            loading="lazy"
            className="primary-image"
            $outOfStock={isOutOfStock}
          />
        )}
        {secondaryImage && (
          <Image
            src={secondaryImage.thumbnail}
            alt={`${product.name}`}
            srcSet={`${secondaryImage.thumbnail} 320w, ${secondaryImage.large} 480w, ${secondaryImage.large} 800w`}
            sizes="(max-width: 320px) 300px, (max-width: 480px) 440px, 800px"
            loading="lazy"
            className="secondary-image"
            $outOfStock={isOutOfStock}
          />
        )}
      </Picture>
      <Bottom>
        <ProductTitle
          onClick={() =>
            navigate(goToProduct(product.category_name, product.name))
          }
        >
          {product.name}
        </ProductTitle>

        <QuantityLabel>{t("PRODUCT_CARD.CHOOSE_QUANTITY")}</QuantityLabel>
        <GridQuantity>
          {[1, 10, 50].map((item, index) => (
            <GridQuantityItem
              key={index}
              type="button"
              $isActive={selectedQuantity === item}
              onClick={() => setSelectedQuantity(item)}
            >
              {item}x
            </GridQuantityItem>
          ))}
        </GridQuantity>

        <PriceBlock>
          <PriceRow>
            <TotalPrice key={`total-${selectedQuantity}`}>
              {currencyTag}
              {(product.price * selectedQuantity).toFixed(2)}
            </TotalPrice>
          </PriceRow>
          <PriceRow>
            <PerPiece key={`piece-${selectedQuantity}`}>
              {currencyTag}
              {discountedPrice.toFixed(2)}/{t("PRODUCT_CARD.PIECE")}
            </PerPiece>
            {savingsPercentage > 0 && (
              <SavingsBadge key={`badge-${selectedQuantity}`}>
                -{savingsPercentage}%
              </SavingsBadge>
            )}
          </PriceRow>
        </PriceBlock>

        <ButtonWrapper>
          <Button
            onClick={() => addToCart(selectedQuantity)}
            disabled={isOutOfStock}
          >
            {!isOutOfStock && (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <path
                  d="M3 3H4.63158L5.72632 15.3789C5.82123 16.4547 6.72259 17.2632 7.80263 17.2632H18.4211C19.4419 17.2632 20.3097 16.5304 20.4847 15.5252L21.7895 8.05263H6.31579"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <circle cx="9" cy="20.5" r="1.3" fill="currentColor" />
                <circle cx="18" cy="20.5" r="1.3" fill="currentColor" />
              </svg>
            )}
            {!isOutOfStock
              ? t("BUTTONS.ADD_TO_CART")
              : t("BUTTONS.AVAILABLE_SOON") + "!"}
          </Button>

          {isAnimating && (
            <AddToCartAnim
              isAnimating={isAnimating}
              onComplete={handleAnimationComplete}
            />
          )}
        </ButtonWrapper>
      </Bottom>
    </Container>
  );
};

export default ProductCard;
